package com.klantenstop.web.pages.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import com.klantenstop.web.components.H1Heading
import com.klantenstop.web.components.H2Heading
import com.klantenstop.web.components.layout.PageLayout
import com.klantenstop.web.styles.Typography
import com.klantenstop.web.utils.Constants.COLOR_MODE_KEY
import com.klantenstop.web.utils.KlaroConsentManager
import com.klantenstop.web.utils.logoImage
import com.materialkobweb.components.Spacer
import com.materialkobweb.components.widgets.DsFlexBox
import com.materialkobweb.components.widgets.FilledButton
import com.materialkobweb.components.widgets.FilledTonalButton
import com.materialkobweb.styles.MaterialColorVars
import com.materialkobweb.utils.rememberViewModel
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flex
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.onMouseOver
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.saveToLocalStorage
import com.varabyte.kobweb.silk.theme.shapes.Rect
import com.varabyte.kobweb.silk.theme.shapes.clip
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

@Page("/instellingen")
@Composable
fun SettingsPage() {
    var colorMode by ColorMode.currentState
    val viewModel = rememberViewModel { SettingsViewModel(colorMode) }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.effect.collectLatest {
            when (it) {
                is SettingsContract.Effect.SelectColorMode -> {
                    colorMode = it.colorMode
                    colorMode.saveToLocalStorage(COLOR_MODE_KEY)
                }

                is SettingsContract.Effect.OpenConsentModal -> KlaroConsentManager.showConsentModal()
            }
        }
    }

    SettingsPageContent(
        state = state,
        onEvent = viewModel::sendIntent
    )
}

@Composable
private fun SettingsPageContent(
    state: SettingsContract.State,
    onEvent: (SettingsContract.Intent) -> Unit
) {
    val breakpoint = rememberBreakpoint()

    PageLayout(
        loginRequired = true,
        title = "Settings"
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth(if (breakpoint > Breakpoint.MD) 80.percent else 90.percent)
                .padding(top = 40.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            H1Heading(
                modifier = Modifier.fillMaxWidth().margin(bottom = 24.px),
                typography = Typography.HeadlineMedium,
                text = "Settings",
                color = MaterialColorVars.Primary.value()
            )

            H2Heading(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 4.px)
                    .fontWeight(FontWeight.Bold)
                    .borderBottom(2.px, LineStyle.Solid, Color.gray),
                typography = Typography.TitleLarge,
                text = "Thema voorkeur"
            )

            ThemePreview(state.hoveringColorMode)

            Row(
                modifier = Modifier.margin(top = 20.px),
                horizontalArrangement = Arrangement.spacedBy(24.px, Alignment.CenterHorizontally)
            ) {
                ThemeOptionButton(
                    colorMode = ColorMode.DARK,
                    currentColorMode = state.currentColorMode,
                    onEvent = onEvent,
                    text = "Dark color"
                )
                ThemeOptionButton(
                    colorMode = ColorMode.LIGHT,
                    currentColorMode = state.currentColorMode,
                    onEvent = onEvent,
                    text = "Light color"
                )
            }

            // Cookie consent section
            CookieConsentSection(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(top = 52.px, bottom = 52.px),
                onManageClick = { onEvent(SettingsContract.Intent.ManageConsent) }
            )
        }
    }
}

@Composable
private fun CookieConsentSection(
    modifier: Modifier = Modifier,
    onManageClick: () -> Unit
) {
    val breakpoint = rememberBreakpoint()

    Column(modifier = modifier) {
        H2Heading(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 4.px)
                .fontWeight(FontWeight.Bold)
                .borderBottom(2.px, LineStyle.Solid, Color.gray),
            typography = Typography.TitleLarge,
            text = "Privacy & Cookies"
        )

        DsFlexBox(
            modifier = Modifier
                .fillMaxWidth()
                .margin(top = 20.px)
                .padding(24.px)
                .backgroundColor(MaterialColorVars.SurfaceContainer.value())
                .borderRadius(12.px)
                .border(1.px, LineStyle.Solid, MaterialColorVars.OutlineVariant.value()),
            flexDirection = if (breakpoint > Breakpoint.SM) FlexDirection.Row else FlexDirection.Column,
            alignItems = if (breakpoint > Breakpoint.SM) AlignItems.Center else AlignItems.FlexStart,
            gap = 24.px
        ) {
            Column(
                modifier = Modifier.flex(1),
                verticalArrangement = Arrangement.spacedBy(16.px)
            ) {
                SpanText(
                    text = "Manage your cookie preferences",
                    modifier = Modifier
                        .fontSize(16.px)
                        .fontWeight(FontWeight.Medium)
                        .color(MaterialColorVars.OnSurface.value())
                )

                SpanText(
                    text = "We use cookies to enhance your experience, analyze site usage, and support our marketing efforts. You can customize your preferences or manage all cookies settings.",
                    modifier = Modifier
                        .fontSize(14.px)
                        .color(MaterialColorVars.OnSurfaceVariant.value())
                        .lineHeight(22.px)
                )
            }

            FilledTonalButton(onClick = onManageClick) {
                Text("Manage Preferences")
            }
        }
    }
}

@Composable
private fun ThemePreview(hoveringColorMode: ColorMode) {
    val breakpoint = rememberBreakpoint()

    Surface(
        modifier = Modifier
            .fillMaxWidth(if (breakpoint > Breakpoint.MD) 40.percent else 60.percent)
            .margin(top = 20.px)
            .border(2.px, LineStyle.Solid, Color.gray)
            .borderRadius(10.px)
            .clip(Rect(8.5.px)),
        colorModeOverride = hoveringColorMode
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            verticalArrangement = Arrangement.spacedBy(0.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Row(
                modifier = Modifier.fillMaxWidth()
                    .height(40.px)
                    .padding(leftRight = 20.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Image(
                    modifier = Modifier.height(24.px),
                    src = logoImage(ColorMode.current),
                    alt = "Logo Image"
                )

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(20.px, Alignment.End),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(modifier = Modifier.width(50.px).height(14.px).borderRadius(20.px).backgroundColor(MaterialColorVars.OnSecondaryContainer.value()))
                    Box(modifier = Modifier.width(50.px).height(14.px).borderRadius(20.px).backgroundColor(MaterialColorVars.OnSecondaryContainer.value()))
                    Box(modifier = Modifier.width(50.px).height(14.px).borderRadius(20.px).backgroundColor(MaterialColorVars.OnSecondaryContainer.value()))
                    Box(modifier = Modifier.width(20.px).height(20.px).borderRadius(50.percent).backgroundColor(MaterialColorVars.OnSecondaryContainer.value()))
                }
            }

            Spacer(modifier = Modifier.height(24.px))

            Row(
                modifier = Modifier,
                horizontalArrangement = Arrangement.spacedBy(20.px)
            ) {
                Box(modifier = Modifier.width(100.px).height(20.px).borderRadius(20.px).backgroundColor(MaterialColorVars.OnBackground.value()))
                Box(modifier = Modifier.width(70.px).height(20.px).borderRadius(20.px).padding(6.px, 20.px).border(1.px, LineStyle.Solid, MaterialColorVars.Outline.value())) {
                    Box(modifier = Modifier.fillMaxSize().borderRadius(20.px).backgroundColor(MaterialColorVars.Primary.value()))
                }

            }
            Spacer(modifier = Modifier.height(50.px))
        }
    }
}

@Composable
private fun ThemeOptionButton(
    colorMode: ColorMode,
    currentColorMode: ColorMode,
    onEvent: (SettingsContract.Intent) -> Unit,
    text: String
) {
    FilledButton(
        modifier = Modifier
            .onMouseOver {
                onEvent(SettingsContract.Intent.UpdateHoveringColorMode(colorMode))
            }
            .onMouseLeave {
                onEvent(SettingsContract.Intent.UpdateHoveringColorMode(currentColorMode))
            },
        onClick = { onEvent(SettingsContract.Intent.SelectColorMode(colorMode)) }
    ) {
        SpanText(text)
    }
}
